import {
  createConfiguredFieldValuesSchema,
  type ConfiguredFieldOptions,
  type ConfiguredFieldValuesInput,
  type ConfiguredFieldValuesSchemaContext,
} from '@cims-online/shared'
import type {
  ConfiguredFieldsValidation,
  ConfiguredFieldsValidationContext,
} from './types'

/** Protects decisions on active options when the action forbids removing them. */
function approvalFieldIssues(
  fields: readonly ConfiguredFieldOptions[],
  values: ConfiguredFieldValuesInput,
  validationContext: ConfiguredFieldsValidationContext | undefined
): Record<string, string[]> {
  const statesByFieldId = new Map(
    validationContext?.approvalFields?.map(({ fieldId, options }) => [
      fieldId,
      options,
    ])
  )
  const issues: Record<string, string[]> = {}
  for (const field of fields) {
    if (field.type !== 'approvals' || field.allowRemoveApprovedOptions) continue
    const options = statesByFieldId.get(field.id)
    if (!options) continue
    const value = values[field.id]
    if (
      !Array.isArray(value) ||
      !value.every((optionId) => typeof optionId === 'string')
    )
      continue

    const selectedOptionIds = new Set(value)
    const activeOptionIds = new Set(
      field.options.filter(({ deleted }) => !deleted).map(({ id }) => id)
    )
    if (
      options.some(
        ({ optionId, state }) =>
          state !== null &&
          activeOptionIds.has(optionId) &&
          !selectedOptionIds.has(optionId)
      )
    )
      issues[field.id] = ['Approval options with a decision cannot be removed']
  }

  return issues
}

/** Rejects historical deleted pay items even when the loaded project list omits them. */
function deletedPayItemIssues(
  fields: readonly ConfiguredFieldOptions[],
  values: ConfiguredFieldValuesInput,
  validationContext: ConfiguredFieldsValidationContext | undefined
): Record<string, string[]> {
  const deletedPayItemIds = new Set(
    validationContext?.deletedPayItemIds?.map((id) => id.toLowerCase())
  )
  if (deletedPayItemIds.size === 0) return {}

  const fieldIssues: Record<string, string[]> = {}
  for (const field of fields) {
    if (field.type !== 'pay_items' || !field.forbidDeleted) continue
    const value = values[field.id]
    if (
      Array.isArray(value) &&
      value.some(
        (optionId) =>
          typeof optionId === 'string' &&
          deletedPayItemIds.has(optionId.toLowerCase())
      )
    )
      fieldIssues[field.id] = ['Deleted pay items are forbidden']
  }
  return fieldIssues
}

/**
 * Validates configured field values with the same shared configured-field
 * schemas used by the API before allowing an overlay to submit.
 */
export function validateConfiguredFields(
  fields: readonly ConfiguredFieldOptions[],
  values: ConfiguredFieldValuesInput,
  fieldValueContexts: ConfiguredFieldValuesSchemaContext = {},
  validationContext?: ConfiguredFieldsValidationContext
): ConfiguredFieldsValidation {
  const valuesResult = createConfiguredFieldValuesSchema(
    fields,
    fieldValueContexts
  ).safeParse(values)
  const fieldIssues: Record<string, string[]> = {}

  if (!valuesResult.success) {
    const fieldIds = new Set(fields.map(({ id }) => id))
    for (const issue of valuesResult.error.issues) {
      const fieldId = issue.path[0]
      if (typeof fieldId === 'string' && fieldIds.has(fieldId))
        (fieldIssues[fieldId] ??= []).push(issue.message)
    }
  }

  for (const issuesByField of [
    approvalFieldIssues(fields, values, validationContext),
    deletedPayItemIssues(fields, values, validationContext),
  ])
    for (const [fieldId, issues] of Object.entries(issuesByField))
      (fieldIssues[fieldId] ??= []).push(...issues)

  const valid = valuesResult.success && Object.keys(fieldIssues).length === 0
  if (!valid)
    return {
      data: undefined,
      fieldIssues,
      valid: false,
    }

  return {
    data: { values: valuesResult.data },
    fieldIssues,
    valid: true,
  }
}
